import EventConstants from '../../../constants/EventConstants'
import { Section } from '../../../models/attributes/Section'
import { View } from '../../../models/view/View'
import { ViewFilter } from '../../../models/view/ViewFilter'
import { AxiosUtil } from '../../../utils/axios-util'
import {
  errorResponseNotification,
  successNotification,
} from '../../notification-service'
import { AddProductViewRequest } from './models/AddProductViewRequest'
import { CopyProductViewRequest } from './models/CopyProductViewRequest'
import { FilterOperator } from './models/FilterOperators'
import { CfImpressionRequest } from './models/CfImpressionRequest'
import { UpdateProductViewRequest } from './models/UpdateProductViewRequest'

const USER_SETTING_ENDPOINT = 'internal/v1/userSetting'

export const getCustomAttributesForUser = ({
  showArchived,
  userId,
  attributeName,
}: {
  showArchived: boolean
  attributeName?: string
  userId?: string
}) => {
  const requestParams = new URLSearchParams({
    global_level: 'true',
    show_archived_custom_attributes: showArchived.toString(),
  })

  if (userId) {
    requestParams.append('user_id', userId)
  }

  if (attributeName) {
    requestParams.append('name', attributeName)
  }

  const url = `${USER_SETTING_ENDPOINT}/getCustomAttributes?${requestParams.toString()}`

  return AxiosUtil.get(url)
    .then(response => response?.data?.attributes)
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const addCustomAttributesForUser = (customAttribute: unknown) => {
  const url = `${USER_SETTING_ENDPOINT}/customAttributes`

  return AxiosUtil.post(url, customAttribute, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then(() => {
      successNotification('Custom Attribute created Successfully')
    })
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const updateCustomAttributesForUser = (
  customAttributeId: string,
  customAttribute: unknown,
) => {
  const url = `${USER_SETTING_ENDPOINT}/updateCustomAttributes/${customAttributeId}`

  return AxiosUtil.put(url, customAttribute, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then(() => {
      successNotification('Custom Attribute updated Successfully')
    })
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const getAttributeColumns = (options?: { numericOnly?: boolean }) => {
  const requestParams = new URLSearchParams({
    includeItemOnlyAttrs: 'true',
  })

  if (options?.numericOnly !== undefined) {
    requestParams.append('numeric_only', options.numericOnly.toString())
  }

  const url = `internal/events/v1/event/product/sections?${requestParams.toString()}`

  return AxiosUtil.get(url)
    .then(response => response?.data?.attributes as Section[])
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const archiveCustomAttributes = ({
  id,
  archive,
}: {
  id: string
  archive: boolean
}) => {
  const requestParams = new URLSearchParams({
    archive_custom_attributes: archive.toString(),
  })

  const url = `${USER_SETTING_ENDPOINT}/archiveCustomAttributes/${id}?${requestParams.toString()}`

  return AxiosUtil.put(
    url,
    {},
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  )
    .then(() => {
      if (archive) {
        successNotification('Custom Attribute Deleted Successfully')
      } else {
        successNotification('Custom Attribute Restored Successfully')
      }
    })
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const copyCustomAttributesForUser = (customAttributeIds: string[]) => {
  const url = `${USER_SETTING_ENDPOINT}/copyCustomAttributes`
  const requestBody = {
    attributeIds: customAttributeIds,
  }

  return AxiosUtil.post(url, requestBody, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then(response => response.data)
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const addProductView = (
  addProductViewRequest: AddProductViewRequest,
) => {
  const url = `${USER_SETTING_ENDPOINT}/addProductView`

  return AxiosUtil.post(url, addProductViewRequest, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then((response: any) => successNotification('Successfully Added View'))
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const copyProductView = (
  copyProductViewRequest: CopyProductViewRequest,
) => {
  const url = `${USER_SETTING_ENDPOINT}/copyProductView`

  return AxiosUtil.post(url, copyProductViewRequest, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then((response: any) => successNotification('Successfully Copied View'))
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const updateProductView = (
  updateProductViewRequest: UpdateProductViewRequest,
) => {
  const url = `${USER_SETTING_ENDPOINT}/updateProductView`

  return AxiosUtil.put(url, updateProductViewRequest, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then((response: any) => successNotification('Successfully Updated View'))
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const getUserProductViews = (
  userId: string,
  type: string,
): Promise<View[]> => {
  return getProductViews({ userId, type })
}

export const getProductViewsWithName = (
  name: string,
  type: string,
): Promise<View[]> => {
  return getProductViews({ viewName: name, type })
}

export const getProductViews = (args: {
  userId?: string
  viewName?: string
  type: string
}): Promise<View[]> => {
  const { userId, viewName, type } = args
  const requestParams = new URLSearchParams({
    type,
  })

  if (userId) {
    requestParams.append('userId', userId)
  }

  if (viewName) {
    requestParams.append('name', viewName)
  }

  const url = `${USER_SETTING_ENDPOINT}/productView?${requestParams.toString()}`

  return AxiosUtil.get<View[]>(url)
    .then(response => response.data)
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const deleteProductView = (viewId: string) => {
  const requestParams = new URLSearchParams({
    viewId,
  })

  const url = `${USER_SETTING_ENDPOINT}/deleteProductView?${requestParams.toString()}`
  return AxiosUtil.delete(url)
    .then((response: any) => successNotification(response.message))
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const updateFiltersForProductView = (
  viewId: number,
  userId: string,
  viewFilters: ViewFilter[],
) => {
  const requestParams = new URLSearchParams({
    viewId: viewId.toString(),
    userId,
  })

  const url = `${USER_SETTING_ENDPOINT}/updateFiltersForProductView?${requestParams.toString()}`

  return AxiosUtil.put(url, viewFilters, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then((response: any) => successNotification(response.message))
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const getFilterOperators = (): Promise<FilterOperator[]> => {
  const url = `${EventConstants.COMMON}/viewType`

  return AxiosUtil.get(url)
    .then(response => response.data)
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const getCfImpressionMappings = (eventId: string) => {
  const url = `${EventConstants.COMMON}getCfImpressionMappingsByEvent/${eventId}`
  return AxiosUtil.get(url)
    .then(res => res.data[0]?.programImpressions ?? [])
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}

export const saveCfImpressionMappings = (request: CfImpressionRequest) => {
  const url = `${EventConstants.COMMON}saveCfImpressionMappings`
  return AxiosUtil.put(url, request)
    .then(res => res.data.programImpressions)
    .catch(err => {
      errorResponseNotification(err)
      throw err
    })
}
